/**
 * Drill primitive library — Demo Review Wave 8.
 *
 * Hand-authored DrillSpec templates that ship as starting points for
 * the Drill Designer. Coaches pick from the library, the chosen spec
 * lands on the active screen via setSpecCmd, then they tweak.
 *
 * Twelve primitives covering the FA / US Soccer canon:
 *   1. Rondo 4v2
 *   2. Possession box 6v6+2
 *   3. Pass-and-move warm-up
 *   4. 1v1 to goal
 *   5. Cross and finish
 *   6. Build-up vs press
 *   7. High press trigger
 *   8. Counter-attack 3v2
 *   9. Corner attack
 *  10. Low-block defending
 *  11. 4v4+GKs transition
 *  12. Dribble weave
 *
 * Coordinate system per types.ts: x = lateral [0, 1], y = goal-to-
 * goal [0, 1] (0 = own goal, 1 = opposition goal).
 *
 * Each template is small and reversible — coach can clear the screen
 * and repick. Library acts as a starting palette, not a binding spec.
 */
import { DRILL_SPEC_SCHEMA_VERSION } from './types';
import type { DrillSpec, NormPoint } from './types';

export type DrillCorner =
  | 'technical_tactical'
  | 'physical'
  | 'psychological'
  | 'social';

export interface DrillTemplate {
  id: string;
  name: string;
  description: string;
  primaryCorner: DrillCorner;
  cornerTags: DrillCorner[];
  /** Suggested duration in minutes — coach can change. */
  durationMins: number;
  /** Minimum players required for the drill as authored. */
  playersRequired: number;
  /** US Soccer phases the drill suits — lifted into the picker filter. */
  ageGroups: ('Foundation' | 'Progression' | 'Performance')[];
  spec: DrillSpec;
}

// Helpers — keep template authoring legible.
const ENT = (
  id: string,
  type: 'home' | 'away' | 'ball',
  position: NormPoint,
  label = '',
  role?: string,
): DrillSpec['entities'][number] => ({ id, type, position, label, role });

const ARROW_PASS = (
  id: string,
  fromEntityId: string,
  toEntityId: string,
): DrillSpec['arrows'][number] => ({
  id,
  from: { kind: 'entity', entityId: fromEntityId },
  to: { kind: 'entity', entityId: toEntityId },
  kind: 'pass',
});

const ARROW_RUN = (
  id: string,
  fromEntityId: string,
  toPosition: NormPoint,
): DrillSpec['arrows'][number] => ({
  id,
  from: { kind: 'entity', entityId: fromEntityId },
  to: { kind: 'point', position: toPosition },
  kind: 'run',
});

const ARROW_DRIBBLE = (
  id: string,
  fromEntityId: string,
  toPosition: NormPoint,
): DrillSpec['arrows'][number] => ({
  id,
  from: { kind: 'entity', entityId: fromEntityId },
  to: { kind: 'point', position: toPosition },
  kind: 'dribble',
});

const ARROW_PRESS = (
  id: string,
  fromEntityId: string,
  toEntityId: string,
): DrillSpec['arrows'][number] => ({
  id,
  from: { kind: 'entity', entityId: fromEntityId },
  to: { kind: 'entity', entityId: toEntityId },
  kind: 'press',
});

const CONE = (
  id: string,
  position: NormPoint,
  variant: 'cone' | 'flat' | 'mannequin' | 'gate' = 'cone',
): DrillSpec['cones'][number] => ({ id, position, variant });

const baseSpec = (overrides: Partial<DrillSpec> = {}): DrillSpec => ({
  schemaVersion: DRILL_SPEC_SCHEMA_VERSION,
  pitchPresetId: 'full_11v11',
  customPitch: null,
  pitchView: 'full',
  entities: [],
  cones: [],
  arrows: [],
  zones: [],
  keyframes: [],
  ...overrides,
});

// ---------- 1. Rondo 4v2 -------------------------------------------
const RONDO_4V2: DrillTemplate = {
  id: 'rondo-4v2',
  name: 'Rondo 4v2',
  description:
    '4 outside players keep possession against 2 defenders in a 10×10 grid. Two-touch limit; switch defenders after a turnover.',
  primaryCorner: 'technical_tactical',
  cornerTags: ['technical_tactical', 'social'],
  durationMins: 12,
  playersRequired: 6,
  ageGroups: ['Foundation', 'Progression', 'Performance'],
  spec: baseSpec({
    pitchView: 'quarter',
    entities: [
      ENT('p1', 'home', [0.30, 0.55], '1'),
      ENT('p2', 'home', [0.70, 0.55], '2'),
      ENT('p3', 'home', [0.30, 0.30], '3'),
      ENT('p4', 'home', [0.70, 0.30], '4'),
      ENT('d1', 'away', [0.45, 0.45], 'A'),
      ENT('d2', 'away', [0.55, 0.40], 'B'),
      ENT('b1', 'ball', [0.30, 0.55]),
    ],
    cones: [
      CONE('c1', [0.25, 0.25]),
      CONE('c2', [0.75, 0.25]),
      CONE('c3', [0.25, 0.60]),
      CONE('c4', [0.75, 0.60]),
    ],
    arrows: [
      ARROW_PASS('a1', 'p1', 'p2'),
      ARROW_PASS('a2', 'p2', 'p4'),
      ARROW_PASS('a3', 'p4', 'p3'),
    ],
  }),
};

// ---------- 2. Possession box 6v6+2 --------------------------------
const POSSESSION_BOX: DrillTemplate = {
  id: 'possession-box-6v6-2',
  name: 'Possession box 6v6+2',
  description:
    '30×25 yard grid. 6v6 inside, 2 neutral floaters always with the team in possession. Score: 6 consecutive passes = 1 point.',
  primaryCorner: 'technical_tactical',
  cornerTags: ['technical_tactical', 'social', 'psychological'],
  durationMins: 18,
  playersRequired: 14,
  ageGroups: ['Progression', 'Performance'],
  spec: baseSpec({
    pitchView: 'quarter',
    entities: [
      // Home 6
      ENT('h1', 'home', [0.25, 0.30], '1'),
      ENT('h2', 'home', [0.45, 0.25], '2'),
      ENT('h3', 'home', [0.65, 0.30], '3'),
      ENT('h4', 'home', [0.30, 0.55], '4'),
      ENT('h5', 'home', [0.50, 0.50], '5'),
      ENT('h6', 'home', [0.70, 0.55], '6'),
      // Away 6
      ENT('a1', 'away', [0.30, 0.40], 'A'),
      ENT('a2', 'away', [0.50, 0.35], 'B'),
      ENT('a3', 'away', [0.70, 0.40], 'C'),
      ENT('a4', 'away', [0.35, 0.60], 'D'),
      ENT('a5', 'away', [0.55, 0.60], 'E'),
      ENT('a6', 'away', [0.65, 0.65], 'F'),
      // Neutrals on the long edges
      ENT('n1', 'home', [0.20, 0.45], 'N1', 'NEU'),
      ENT('n2', 'home', [0.80, 0.45], 'N2', 'NEU'),
      ENT('b1', 'ball', [0.25, 0.30]),
    ],
    cones: [
      CONE('c1', [0.18, 0.20]),
      CONE('c2', [0.82, 0.20]),
      CONE('c3', [0.18, 0.70]),
      CONE('c4', [0.82, 0.70]),
    ],
    arrows: [
      ARROW_PASS('a1', 'h1', 'n1'),
      ARROW_PASS('a2', 'n1', 'h4'),
    ],
  }),
};

// ---------- 3. Pass-and-move warm-up -------------------------------
const PASS_AND_MOVE: DrillTemplate = {
  id: 'pass-and-move-warmup',
  name: 'Pass & move warm-up',
  description:
    'Two lines facing each other 12 yards apart. Pass straight, follow your pass to the back of the opposite line. After 2 minutes switch to receive-set-pass on a third player.',
  primaryCorner: 'technical_tactical',
  cornerTags: ['technical_tactical', 'physical'],
  durationMins: 8,
  playersRequired: 8,
  ageGroups: ['Foundation', 'Progression'],
  spec: baseSpec({
    pitchView: 'quarter',
    entities: [
      ENT('p1', 'home', [0.30, 0.30], '1'),
      ENT('p2', 'home', [0.30, 0.40], '2'),
      ENT('p3', 'home', [0.30, 0.50], '3'),
      ENT('p4', 'home', [0.30, 0.60], '4'),
      ENT('p5', 'home', [0.70, 0.30], '5'),
      ENT('p6', 'home', [0.70, 0.40], '6'),
      ENT('p7', 'home', [0.70, 0.50], '7'),
      ENT('p8', 'home', [0.70, 0.60], '8'),
      ENT('b1', 'ball', [0.30, 0.30]),
    ],
    arrows: [
      ARROW_PASS('a1', 'p1', 'p5'),
      ARROW_RUN('a2', 'p1', [0.70, 0.65]),
      ARROW_PASS('a3', 'p5', 'p2'),
      ARROW_RUN('a4', 'p5', [0.30, 0.65]),
    ],
  }),
};

// ---------- 4. 1v1 to goal -----------------------------------------
const ONE_V_ONE: DrillTemplate = {
  id: '1v1-to-goal',
  name: '1v1 to goal',
  description:
    'Striker receives at halfway from a serving coach, dribbles at the defender. Defender starts level with the striker. First touch sets the move; defender wins after 6 seconds if striker hasn’t shot.',
  primaryCorner: 'technical_tactical',
  cornerTags: ['technical_tactical', 'psychological'],
  durationMins: 14,
  playersRequired: 4,
  ageGroups: ['Progression', 'Performance'],
  spec: baseSpec({
    pitchView: 'half_attacking',
    entities: [
      ENT('gk', 'home', [0.50, 0.95], 'GK', 'GK'),
      ENT('st', 'home', [0.50, 0.55], '9', 'ST'),
      ENT('df', 'away', [0.50, 0.65], 'A', 'CB'),
      ENT('coach', 'home', [0.10, 0.50], 'C', 'Server'),
      ENT('b1', 'ball', [0.10, 0.50]),
    ],
    arrows: [
      ARROW_PASS('a1', 'coach', 'st'),
      ARROW_DRIBBLE('a2', 'st', [0.55, 0.85]),
      ARROW_RUN('a3', 'df', [0.55, 0.75]),
    ],
  }),
};

// ---------- 5. Cross and finish ------------------------------------
const CROSS_AND_FINISH: DrillTemplate = {
  id: 'cross-and-finish',
  name: 'Cross and finish',
  description:
    'Winger drives the line, crosses on the second touch. Three runners attack near post, far post, and trailing edge. Server pressure: a defender starts on the cover line.',
  primaryCorner: 'technical_tactical',
  cornerTags: ['technical_tactical', 'physical'],
  durationMins: 16,
  playersRequired: 6,
  ageGroups: ['Progression', 'Performance'],
  spec: baseSpec({
    pitchView: 'half_attacking',
    entities: [
      ENT('gk', 'away', [0.50, 0.95], 'A', 'GK'),
      ENT('rw', 'home', [0.85, 0.65], '7', 'RW'),
      ENT('np', 'home', [0.55, 0.80], '9', 'ST'),
      ENT('fp', 'home', [0.40, 0.80], '11', 'LW'),
      ENT('te', 'home', [0.45, 0.65], '8', 'CM'),
      ENT('cb', 'away', [0.50, 0.85], 'B', 'CB'),
      ENT('b1', 'ball', [0.85, 0.65]),
    ],
    arrows: [
      ARROW_DRIBBLE('a1', 'rw', [0.92, 0.80]),
      ARROW_RUN('a2', 'np', [0.55, 0.90]),
      ARROW_RUN('a3', 'fp', [0.40, 0.90]),
      ARROW_RUN('a4', 'te', [0.50, 0.72]),
      ARROW_PASS('a5', 'rw', 'np'),
    ],
  }),
};

// ---------- 6. Build-up vs press -----------------------------------
const BUILD_UP_VS_PRESS: DrillTemplate = {
  id: 'build-up-vs-press',
  name: 'Build-up vs press (4+1 v 3)',
  description:
    'Back four + GK build out vs three forwards pressing. Pivot drops between the centre-backs to create a 5v3 numerical advantage. Score: a clean pass into the central midfielder past the press.',
  primaryCorner: 'technical_tactical',
  cornerTags: ['technical_tactical', 'social'],
  durationMins: 18,
  playersRequired: 9,
  ageGroups: ['Progression', 'Performance'],
  spec: baseSpec({
    pitchView: 'half_defending',
    entities: [
      ENT('gk', 'home', [0.50, 0.05], '1', 'GK'),
      ENT('rb', 'home', [0.85, 0.20], '2', 'RB'),
      ENT('rcb', 'home', [0.62, 0.18], '5', 'RCB'),
      ENT('lcb', 'home', [0.38, 0.18], '4', 'LCB'),
      ENT('lb', 'home', [0.15, 0.20], '3', 'LB'),
      ENT('cm', 'home', [0.50, 0.40], '6', 'CDM'),
      ENT('rf', 'away', [0.65, 0.30], 'A', 'RF'),
      ENT('cf', 'away', [0.50, 0.32], 'B', 'CF'),
      ENT('lf', 'away', [0.35, 0.30], 'C', 'LF'),
      ENT('b1', 'ball', [0.50, 0.05]),
    ],
    arrows: [
      ARROW_PRESS('a1', 'cf', 'gk'),
      ARROW_RUN('a2', 'cm', [0.50, 0.20]),
      ARROW_PASS('a3', 'gk', 'cm'),
      ARROW_PASS('a4', 'cm', 'rcb'),
    ],
  }),
};

// ---------- 7. High press trigger ----------------------------------
const HIGH_PRESS: DrillTemplate = {
  id: 'high-press-trigger',
  name: 'High press trigger',
  description:
    'Front three press the back four on the trigger pass to the wide CB. Striker curves to cut the central pass; near winger jumps to the ball-side; far winger tucks in to mark the pivot.',
  primaryCorner: 'technical_tactical',
  cornerTags: ['technical_tactical', 'physical', 'social'],
  durationMins: 16,
  playersRequired: 11,
  ageGroups: ['Progression', 'Performance'],
  spec: baseSpec({
    pitchView: 'full',
    entities: [
      ENT('gk', 'away', [0.50, 0.05], 'A', 'GK'),
      ENT('rb', 'away', [0.85, 0.18], 'B', 'RB'),
      ENT('rcb', 'away', [0.62, 0.16], 'C', 'RCB'),
      ENT('lcb', 'away', [0.38, 0.16], 'D', 'LCB'),
      ENT('lb', 'away', [0.15, 0.18], 'E', 'LB'),
      ENT('cm', 'away', [0.50, 0.32], 'F', 'CDM'),
      ENT('cf', 'home', [0.50, 0.28], '9', 'CF'),
      ENT('rw', 'home', [0.70, 0.30], '7', 'RW'),
      ENT('lw', 'home', [0.30, 0.30], '11', 'LW'),
      ENT('rcm', 'home', [0.65, 0.45], '8', 'RCM'),
      ENT('lcm', 'home', [0.35, 0.45], '10', 'LCM'),
      ENT('b1', 'ball', [0.62, 0.16]),
    ],
    arrows: [
      ARROW_PRESS('a1', 'cf', 'rcb'),
      ARROW_PRESS('a2', 'rw', 'rb'),
      ARROW_RUN('a3', 'lw', [0.40, 0.32]),
      ARROW_PRESS('a4', 'rcm', 'cm'),
    ],
  }),
};

// ---------- 8. Counter-attack 3v2 ----------------------------------
const COUNTER_3V2: DrillTemplate = {
  id: 'counter-3v2',
  name: 'Counter-attack 3v2',
  description:
    '3 attackers break from halfway against 2 recovering defenders + GK. 8-second clock — finish or lose possession. Switch roles each round.',
  primaryCorner: 'technical_tactical',
  cornerTags: ['technical_tactical', 'physical', 'psychological'],
  durationMins: 16,
  playersRequired: 6,
  ageGroups: ['Progression', 'Performance'],
  spec: baseSpec({
    pitchView: 'half_attacking',
    entities: [
      ENT('gk', 'away', [0.50, 0.95], 'A', 'GK'),
      ENT('st', 'home', [0.50, 0.55], '9', 'ST'),
      ENT('rw', 'home', [0.78, 0.55], '7', 'RW'),
      ENT('lw', 'home', [0.22, 0.55], '11', 'LW'),
      ENT('rcb', 'away', [0.58, 0.70], 'B', 'RCB'),
      ENT('lcb', 'away', [0.42, 0.70], 'C', 'LCB'),
      ENT('b1', 'ball', [0.50, 0.55]),
    ],
    arrows: [
      ARROW_DRIBBLE('a1', 'st', [0.50, 0.72]),
      ARROW_RUN('a2', 'rw', [0.78, 0.85]),
      ARROW_RUN('a3', 'lw', [0.22, 0.85]),
      ARROW_PASS('a4', 'st', 'rw'),
    ],
  }),
};

// ---------- 9. Corner attack ---------------------------------------
const CORNER_ATTACK: DrillTemplate = {
  id: 'corner-attack',
  name: 'Corner attack — near-post flick',
  description:
    'In-swinging corner aimed for the near-post runner to flick on. Trailing runner attacks the centre; back-stick defender holds depth. Practise with 4 attackers vs 4 defenders + GK.',
  primaryCorner: 'technical_tactical',
  cornerTags: ['technical_tactical', 'social'],
  durationMins: 12,
  playersRequired: 9,
  ageGroups: ['Progression', 'Performance'],
  spec: baseSpec({
    pitchView: 'half_attacking',
    entities: [
      ENT('gk', 'away', [0.50, 0.97], 'A', 'GK'),
      ENT('taker', 'home', [0.99, 0.93], '7', 'CK'),
      ENT('np', 'home', [0.62, 0.88], '9', 'Near'),
      ENT('cm', 'home', [0.50, 0.84], '8', 'Mid'),
      ENT('fp', 'home', [0.38, 0.88], '11', 'Far'),
      ENT('edge', 'home', [0.50, 0.74], '6', 'Edge'),
      ENT('d1', 'away', [0.60, 0.90], 'B'),
      ENT('d2', 'away', [0.45, 0.88], 'C'),
      ENT('d3', 'away', [0.40, 0.92], 'D'),
      ENT('d4', 'away', [0.30, 0.85], 'E'),
      ENT('b1', 'ball', [0.99, 0.93]),
    ],
    arrows: [
      ARROW_PASS('a1', 'taker', 'np'),
      ARROW_RUN('a2', 'np', [0.55, 0.90]),
      ARROW_RUN('a3', 'cm', [0.50, 0.92]),
      ARROW_RUN('a4', 'fp', [0.42, 0.92]),
    ],
  }),
};

// ---------- 10. Low-block defending --------------------------------
const LOW_BLOCK: DrillTemplate = {
  id: 'low-block-defending',
  name: 'Low-block defending shape',
  description:
    'Back four + back-four-of-mids hold a compact 4-4-2 just outside the penalty area. Forwards screen the central pass; full-backs stay narrow until the ball goes wide.',
  primaryCorner: 'technical_tactical',
  cornerTags: ['technical_tactical', 'social', 'psychological'],
  durationMins: 16,
  playersRequired: 10,
  ageGroups: ['Progression', 'Performance'],
  spec: baseSpec({
    pitchView: 'half_defending',
    entities: [
      ENT('gk', 'home', [0.50, 0.05], '1', 'GK'),
      ENT('rb', 'home', [0.78, 0.18], '2', 'RB'),
      ENT('rcb', 'home', [0.58, 0.16], '5', 'RCB'),
      ENT('lcb', 'home', [0.42, 0.16], '4', 'LCB'),
      ENT('lb', 'home', [0.22, 0.18], '3', 'LB'),
      ENT('rm', 'home', [0.78, 0.32], '7', 'RM'),
      ENT('rcm', 'home', [0.58, 0.30], '6', 'RCM'),
      ENT('lcm', 'home', [0.42, 0.30], '8', 'LCM'),
      ENT('lm', 'home', [0.22, 0.32], '11', 'LM'),
      ENT('cf', 'home', [0.50, 0.45], '9', 'CF'),
    ],
    arrows: [
      ARROW_COVER('cv1', 'rcm', 'rcb'),
      ARROW_COVER('cv2', 'lcm', 'lcb'),
    ],
  }),
};

function ARROW_COVER(
  id: string,
  fromEntityId: string,
  toEntityId: string,
): DrillSpec['arrows'][number] {
  return {
    id,
    from: { kind: 'entity', entityId: fromEntityId },
    to: { kind: 'entity', entityId: toEntityId },
    kind: 'cover',
  };
}

// ---------- 11. 4v4+GKs transition ---------------------------------
const FOUR_V_FOUR_TRANSITION: DrillTemplate = {
  id: '4v4-gks-transition',
  name: '4v4 + GKs transition',
  description:
    'Two teams of 4 + GKs on a 35×40 grid. After every goal/turnover the transition starts immediately — no reset. Bonus point for finishing within 6 seconds of winning the ball.',
  primaryCorner: 'technical_tactical',
  cornerTags: ['technical_tactical', 'physical', 'psychological'],
  durationMins: 20,
  playersRequired: 10,
  ageGroups: ['Progression', 'Performance'],
  spec: baseSpec({
    pitchView: 'full',
    entities: [
      ENT('hgk', 'home', [0.50, 0.05], '1', 'GK'),
      ENT('h1', 'home', [0.30, 0.30], '2'),
      ENT('h2', 'home', [0.70, 0.30], '3'),
      ENT('h3', 'home', [0.40, 0.50], '4'),
      ENT('h4', 'home', [0.60, 0.50], '5'),
      ENT('agk', 'away', [0.50, 0.95], 'A', 'GK'),
      ENT('a1', 'away', [0.30, 0.70], 'B'),
      ENT('a2', 'away', [0.70, 0.70], 'C'),
      ENT('a3', 'away', [0.40, 0.55], 'D'),
      ENT('a4', 'away', [0.60, 0.55], 'E'),
      ENT('b1', 'ball', [0.50, 0.50]),
    ],
    arrows: [],
  }),
};

// ---------- 12. Dribble weave --------------------------------------
const DRIBBLE_WEAVE: DrillTemplate = {
  id: 'dribble-weave',
  name: 'Dribble weave',
  description:
    'Six cones in a straight line 2 yards apart. Player dribbles through with both feet (inside-outside), repeats with the other foot leading. Add a pass to a teammate at the end on round 2.',
  primaryCorner: 'technical_tactical',
  cornerTags: ['technical_tactical', 'physical'],
  durationMins: 8,
  playersRequired: 4,
  ageGroups: ['Foundation', 'Progression'],
  spec: baseSpec({
    pitchView: 'quarter',
    entities: [
      ENT('p1', 'home', [0.20, 0.50], '1'),
      ENT('p2', 'home', [0.20, 0.30], '2'),
      ENT('p3', 'home', [0.85, 0.50], '3'),
      ENT('b1', 'ball', [0.20, 0.50]),
    ],
    cones: [
      CONE('c1', [0.30, 0.50]),
      CONE('c2', [0.40, 0.50]),
      CONE('c3', [0.50, 0.50]),
      CONE('c4', [0.60, 0.50]),
      CONE('c5', [0.70, 0.50]),
      CONE('c6', [0.78, 0.50]),
    ],
    arrows: [
      ARROW_DRIBBLE('a1', 'p1', [0.78, 0.50]),
      ARROW_PASS('a2', 'p1', 'p3'),
    ],
  }),
};

export const DRILL_LIBRARY: readonly DrillTemplate[] = [
  RONDO_4V2,
  POSSESSION_BOX,
  PASS_AND_MOVE,
  ONE_V_ONE,
  CROSS_AND_FINISH,
  BUILD_UP_VS_PRESS,
  HIGH_PRESS,
  COUNTER_3V2,
  CORNER_ATTACK,
  LOW_BLOCK,
  FOUR_V_FOUR_TRANSITION,
  DRIBBLE_WEAVE,
] as const;

/** Lookup a template by id. Returns null when missing. */
export function getDrillTemplate(id: string): DrillTemplate | null {
  return DRILL_LIBRARY.find((t) => t.id === id) ?? null;
}

/** Filter templates by primary corner — used by the picker tabs. */
export function templatesByCorner(corner: DrillCorner): DrillTemplate[] {
  return DRILL_LIBRARY.filter((t) => t.primaryCorner === corner);
}

/** Filter templates by US Soccer phase suitability. */
export function templatesForPhase(
  phase: 'Foundation' | 'Progression' | 'Performance',
): DrillTemplate[] {
  return DRILL_LIBRARY.filter((t) => t.ageGroups.includes(phase));
}
